import { useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import {
  applyPipelineProposal,
  postPipelineChatMessage,
  rejectPipelineProposal,
} from '@/lib/ai-pipeline-api';
import { usePipelineChatStore } from '@/store/pipeline-chat-store';
import type { PipelineChatMode } from '@/types/ai-pipeline';
import type { PipelineEdge, PipelineNode } from '@/pages/pipelines/types/pipeline';
import type { ValidationResult } from '@/lib/validation-types';

function generateId(): string {
  return crypto.randomUUID?.() || Math.random().toString(36).slice(2, 11);
}

interface UsePipelineChatOptions {
  pipelineId: string | null;
  selectedNodeId?: string | null;
  nodes?: PipelineNode[];
  edges?: PipelineEdge[];
  validationResult?: ValidationResult;
  onProposalApplied?: () => Promise<void> | void;
}

function buildContextAwareMessage(
  userMessage: string,
  options: {
    mode: PipelineChatMode;
    selectedNode: PipelineNode | null;
    nodes: PipelineNode[];
    edges: PipelineEdge[];
    validationResult?: ValidationResult;
  },
): string {
  const selectedNode = options.selectedNode;
  const nodeCounts = options.nodes.reduce<Record<string, number>>((acc, node) => {
    const key = String(node.data?.type || node.type || 'UNKNOWN').toUpperCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const sourceCount = nodeCounts.SOURCE || 0;
  const transformCount =
    (nodeCounts.TRANSFORMATION || 0) +
    (nodeCounts.FILTER || 0) +
    (nodeCounts.AGGREGATION || 0) +
    (nodeCounts.JOIN || 0) +
    (nodeCounts.UNION || 0);
  const destinationCount = nodeCounts.DESTINATION || 0;

  const topErrors = options.validationResult?.errors
    ?.slice(0, 3)
    .map((issue) => issue.message)
    .join(' | ');
  const topWarnings = options.validationResult?.warnings
    ?.slice(0, 3)
    .map((issue) => issue.message)
    .join(' | ');

  const selectedConfigSnippet = selectedNode?.data?.config
    ? JSON.stringify(selectedNode.data.config).slice(0, 400)
    : '';

  const contextLines = [
    '[BUILDER_CONTEXT]',
    `mode=${options.mode}`,
    `selectedNodeId=${selectedNode?.id || 'none'}`,
    `selectedNodeType=${selectedNode?.data?.type || 'none'}`,
    `graph.nodes=${options.nodes.length}`,
    `graph.edges=${options.edges.length}`,
    `graph.sources=${sourceCount}`,
    `graph.transforms=${transformCount}`,
    `graph.destinations=${destinationCount}`,
    `validation.healthScore=${options.validationResult?.healthScore ?? 'unknown'}`,
    `validation.errors=${options.validationResult?.errors.length ?? 0}`,
    `validation.warnings=${options.validationResult?.warnings.length ?? 0}`,
    `validation.topErrors=${topErrors || 'none'}`,
    `validation.topWarnings=${topWarnings || 'none'}`,
    `selectedNode.config=${selectedConfigSnippet || 'none'}`,
    '[/BUILDER_CONTEXT]',
  ].join('\n');

  return `${contextLines}\n\n[USER_REQUEST]\n${userMessage}\n[/USER_REQUEST]`;
}

export function usePipelineChat({
  pipelineId,
  selectedNodeId,
  nodes = [],
  edges = [],
  validationResult,
  onProposalApplied,
}: UsePipelineChatOptions) {
  const pipelineKey = pipelineId || 'draft-builder';

  const ensurePipeline = usePipelineChatStore((state) => state.ensurePipeline);
  const setMode = usePipelineChatStore((state) => state.setMode);
  const appendMessage = usePipelineChatStore((state) => state.appendMessage);
  const setProposal = usePipelineChatStore((state) => state.setProposal);
  const setProposals = usePipelineChatStore((state) => state.setProposals);
  const setActiveProposal = usePipelineChatStore(
    (state) => state.setActiveProposal,
  );
  const setLoading = usePipelineChatStore((state) => state.setLoading);
  const clearPipeline = usePipelineChatStore((state) => state.clearPipeline);

  const entry = usePipelineChatStore((state) => state.byPipeline[pipelineKey]);

  useEffect(() => {
    ensurePipeline(pipelineKey);
  }, [ensurePipeline, pipelineKey]);

  const mode: PipelineChatMode = entry?.mode ?? 'GLOBAL';
  const messages = entry?.messages ?? [];
  const proposal = entry?.proposal ?? null;
  const proposals = entry?.proposals ?? [];
  const isLoading = entry?.isLoading ?? false;

  const canSend = useMemo(() => {
    if (isLoading) return false;
    if (!pipelineId) return false;
    if (mode === 'SELECTION' && !selectedNodeId) return false;
    return true;
  }, [isLoading, mode, pipelineId, selectedNodeId]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );

  const contextSummary = useMemo(
    () => ({
      selectedNodeLabel: selectedNode?.data?.label || selectedNode?.id || null,
      selectedNodeType: selectedNode?.data?.type || null,
      nodeCount: nodes.length,
      edgeCount: edges.length,
      healthScore: validationResult?.healthScore ?? null,
      errorCount: validationResult?.errors.length ?? 0,
      warningCount: validationResult?.warnings.length ?? 0,
    }),
    [selectedNode, nodes.length, edges.length, validationResult],
  );

  const suggestedPrompts = useMemo(() => {
    const prompts: string[] = [];
    if (selectedNode?.data?.type === 'SOURCE') {
      prompts.push('Optimalkan node SOURCE ini dengan query/filter yang lebih efisien.');
    }
    if (selectedNode?.data?.type === 'TRANSFORMATION') {
      prompts.push('Usulkan transformasi cleansing dan normalisasi terbaik untuk node ini.');
    }
    if ((validationResult?.errors.length || 0) > 0) {
      prompts.push('Perbaiki error validasi pipeline ini tanpa mengubah tujuan utama data.');
    }
    prompts.push('Susun ulang pipeline agar lebih ringkas namun tetap robust untuk eksekusi produksi.');
    return prompts.slice(0, 3);
  }, [selectedNode?.data?.type, validationResult?.errors.length]);

  const sendMessage = async (text: string) => {
    const message = text.trim();
    if (!message) return;

    if (!pipelineId) {
      toast.error('Save pipeline first before using builder chat.');
      return;
    }

    if (mode === 'SELECTION' && !selectedNodeId) {
      toast.error('Select a node first to use selection mode.');
      return;
    }

    appendMessage(pipelineKey, {
      id: generateId(),
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
    });
    setLoading(pipelineKey, true);

    try {
      const enrichedMessage = buildContextAwareMessage(message, {
        mode,
        selectedNode,
        nodes,
        edges,
        validationResult,
      });

      const response = await postPipelineChatMessage(pipelineId, {
        message: enrichedMessage,
        mode,
        selection:
          mode === 'SELECTION' && selectedNodeId
            ? { nodeId: selectedNodeId }
            : undefined,
      });

      const nextProposals =
        response.data.proposals && response.data.proposals.length > 0
          ? response.data.proposals
          : [response.data.proposal];
      setProposals(pipelineKey, nextProposals);
      const nextProposal = nextProposals[0];

      appendMessage(pipelineKey, {
        id: generateId(),
        role: 'assistant',
        content:
          nextProposal.insights?.[0] ||
          `Proposal generated with ${nextProposal.operations.length} operation(s).`,
        createdAt: new Date().toISOString(),
      });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to generate proposal');
    } finally {
      setLoading(pipelineKey, false);
    }
  };

  const applyProposal = async (selectedOperationIndexes?: number[]) => {
    if (!pipelineId || !proposal?.id) {
      toast.error('No proposal to apply.');
      return false;
    }

    setLoading(pipelineKey, true);
    try {
      await applyPipelineProposal(pipelineId, proposal.id, {
        selectedOperationIndexes,
      });
      if (onProposalApplied) {
        await onProposalApplied();
      }
      setProposal(pipelineKey, null);
      appendMessage(pipelineKey, {
        id: generateId(),
        role: 'assistant',
        content: 'Proposal applied successfully and builder view has been refreshed.',
        createdAt: new Date().toISOString(),
      });
      toast.success('Proposal applied and canvas updated.');
      return true;
    } catch (error: any) {
      toast.error(error?.message || 'Failed to apply proposal');
      return false;
    } finally {
      setLoading(pipelineKey, false);
    }
  };

  const rejectProposal = async () => {
    if (!pipelineId || !proposal?.id) {
      toast.error('No proposal to reject.');
      return false;
    }

    setLoading(pipelineKey, true);
    try {
      await rejectPipelineProposal(pipelineId, proposal.id);
      setProposal(pipelineKey, null);
      appendMessage(pipelineKey, {
        id: generateId(),
        role: 'assistant',
        content: 'Proposal rejected. Send a new message to regenerate.',
        createdAt: new Date().toISOString(),
      });
      toast.success('Proposal rejected.');
      return true;
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reject proposal');
      return false;
    } finally {
      setLoading(pipelineKey, false);
    }
  };

  return {
    pipelineKey,
    mode,
    messages,
    proposal,
    proposals,
    isLoading,
    canSend,
    contextSummary,
    suggestedPrompts,
    setMode: (nextMode: PipelineChatMode) => setMode(pipelineKey, nextMode),
    setActiveProposal: (proposalId: string) =>
      setActiveProposal(pipelineKey, proposalId),
    sendMessage,
    applyProposal,
    rejectProposal,
    clearChat: () => clearPipeline(pipelineKey),
  };
}
